<template>
    <v-card class="settings-card">
        <div class="card-section-header px-6 py-4">
            <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                    <v-icon class="mr-2" color="white" size="18">mdi-calendar-question</v-icon>
                    <span class="card-section-title">Supplier Availability</span>
                    <span v-for="(count, key) in counts" :key="key" class="avail-chip ml-2"
                        :style="{ background: status[key].color, color: status[key].text }">
                        {{ count }} {{ status[key].label }}
                    </span>
                </div>
                <div class="d-flex align-center" style="gap:8px;">
                    <v-btn small depressed dark color="rgba(255,255,255,0.2)" @click="openLog()">
                        <v-icon small left>mdi-phone-log</v-icon>
                        Log Contact
                    </v-btn>
                    <v-btn small depressed dark color="rgba(255,255,255,0.2)" @click="openRequest()">
                        <v-icon small left>mdi-email-fast-outline</v-icon>
                        Request Availability
                    </v-btn>
                </div>
            </div>
            <div class="card-section-subtitle">Who was asked whether they can take this group, and what they said</div>
        </div>
        <v-card-text>
            <v-data-table :headers="headers" :items="requests" :loading="loading" dense
                :items-per-page="10" no-data-text="No suppliers asked yet">
                <template v-slot:item.supplier="{ item }">
                    <div class="font-weight-bold">{{ item.supplier ? item.supplier.supplier_name : '—' }}</div>
                    <div class="avail-meta">{{ item.supplier_type ? item.supplier_type.name : '' }}</div>
                    <div v-if="item.products && item.products.length" class="avail-meta">
                        {{ item.products.map(p => p.name).join(', ') }}
                    </div>
                </template>
                <template v-slot:item.requested_date="{ item }">
                    {{ item.requested_date ? $date(item.requested_date).format('DD/MM/YYYY') : '—' }}
                </template>
                <template v-slot:item.contacted="{ item }">
                    <div>{{ channels[item.channel] || item.channel }} · {{ $date(item.created_at).format('DD/MM/YYYY HH:mm') }}</div>
                    <div class="avail-meta">
                        {{ item.sender ? item.sender.name : '' }}<span v-if="item.to_email"> → {{ item.to_email }}</span>
                    </div>
                </template>
                <template v-slot:item.status="{ item }">
                    <span class="avail-chip" :style="{ background: status[item.status].color, color: status[item.status].text }">
                        <v-icon x-small :color="status[item.status].text" class="mr-1">{{ status[item.status].icon }}</v-icon>
                        {{ status[item.status].label }}
                    </span>
                    <div v-if="item.responded_at" class="avail-meta">
                        {{ item.responded_via === 'link' ? 'Supplier' : (item.responder ? item.responder.name : 'Staff') }}
                        · {{ $date(item.responded_at).format('DD/MM/YYYY HH:mm') }}
                    </div>
                </template>
                <template v-slot:item.note="{ item }">
                    <div v-if="item.response_note" style="white-space:pre-line;">“{{ item.response_note }}”</div>
                    <div v-if="item.notes" class="avail-meta" style="white-space:pre-line;">{{ item.notes }}</div>
                </template>
                <template v-slot:item.action="{ item }">
                    <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item v-for="(s, key) in status" :key="key" :disabled="item.status === key"
                                @click="update(item, { status: key })">
                                <v-list-item-title>
                                    <v-icon small :color="s.text" class="mr-2">{{ s.icon }}</v-icon>Mark {{ s.label }}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="openNotes(item)">
                                <v-list-item-title><v-icon small class="mr-2">mdi-note-edit-outline</v-icon>Edit Notes</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="item.channel === 'email'" @click="viewing = item">
                                <v-list-item-title><v-icon small class="mr-2">mdi-email-open-outline</v-icon>View Email</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="openRequest(item)">
                                <v-list-item-title><v-icon small class="mr-2">mdi-email-sync-outline</v-icon>Ask Again</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="remove(item)">
                                <v-list-item-title class="red--text"><v-icon small color="red" class="mr-2">mdi-delete-outline</v-icon>Delete</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </v-card-text>

        <RequestAvailabilityDialog v-model="request_dialog" :lead="lead" :types="types" :location-name="locationName"
            :preset-type-id="preset.type_id" :preset-supplier-ids="preset.supplier_ids"
            :preset-product-ids="preset.product_ids" :builds="builds" only-preset @sent="requests = $event" />

        <!-- Log a phone call / other contact -->
        <v-dialog v-model="log_dialog" max-width="520px" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Log Contact</v-card-title>
                <v-card-text>
                    <v-select v-model="log.supplier_type_id" :items="[{ id: null, name: 'All categories' }, ...types]"
                        item-text="name" item-value="id" label="Category" outlined dense @change="loadLogSuppliers()" />
                    <v-autocomplete v-model="log.supplier_id" :items="log_suppliers" item-text="supplier_name" item-value="id"
                        label="Supplier" outlined dense :loading="log_loading" />
                    <v-row dense>
                        <v-col cols="6">
                            <v-select v-model="log.channel" :items="[{ value: 'phone', text: 'Phone' }, { value: 'other', text: 'Other' }]"
                                label="How" outlined dense />
                        </v-col>
                        <v-col cols="6">
                            <v-select v-model="log.status" :items="statusItems" label="Answer" outlined dense />
                        </v-col>
                    </v-row>
                    <v-text-field v-model="log.requested_date" type="date" label="Date asked about" outlined dense />
                    <v-textarea v-model="log.notes" label="Notes" outlined dense rows="3" hide-details />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="log_dialog = false">Cancel</v-btn>
                    <v-btn color="#3988AA" dark :disabled="!log.supplier_id" :loading="saving" @click="saveLog()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Staff notes -->
        <v-dialog v-model="notes_dialog" max-width="480px">
            <v-card v-if="notes_item">
                <v-card-title class="text-subtitle-1 font-weight-bold">Notes</v-card-title>
                <v-card-text><v-textarea v-model="notes_text" outlined rows="4" hide-details /></v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="notes_dialog = false">Cancel</v-btn>
                    <v-btn color="#3988AA" dark :loading="saving" @click="saveNotes()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- The email that was sent -->
        <v-dialog :value="!!viewing" max-width="720px" @input="viewing = null">
            <v-card v-if="viewing">
                <v-card-title class="text-subtitle-1 font-weight-bold">{{ viewing.subject }}</v-card-title>
                <v-card-subtitle>To {{ viewing.to_email }}</v-card-subtitle>
                <v-card-text><div v-html="viewing.body"></div></v-card-text>
                <v-card-actions><v-spacer /><v-btn text @click="viewing = null">Close</v-btn></v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import RequestAvailabilityDialog from './RequestAvailabilityDialog.vue'
import { CHANNEL_LABELS, STATUS } from '../../../utils/supplierAvailability'

// Supplier availability for one lead (docs/reference/SUPPLIER_AVAILABILITY.md). Used on the
// Lead page and Event → Suppliers (the event's lead).
export default {
    components: { RequestAvailabilityDialog },
    props: {
        lead: { type: Object, required: true },
        locationName: { type: String, default: 'this location' },
        // The event's booked services, for the request's {services} table (Event → Suppliers).
        builds: { type: Array, default: () => [] },
    },
    data: () => ({
        requests: [],
        types: [],
        loading: false,
        saving: false,
        status: STATUS,
        channels: CHANNEL_LABELS,
        headers: [
            { text: 'Supplier', value: 'supplier', sortable: false },
            { text: 'Date', value: 'requested_date', width: '100px' },
            { text: 'Contacted', value: 'contacted', sortable: false },
            { text: 'Answer', value: 'status', width: '170px' },
            { text: 'Notes', value: 'note', sortable: false },
            { text: '', value: 'action', width: '50px', sortable: false },
        ],
        request_dialog: false,
        preset: { type_id: null, supplier_ids: [], product_ids: [] },
        log_dialog: false,
        log_loading: false,
        log_suppliers: [],
        log: {},
        notes_dialog: false,
        notes_item: null,
        notes_text: '',
        viewing: null,
    }),
    computed: {
        counts() {
            const counts = {}
            this.requests.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1 })
            return counts
        },
        statusItems() {
            return Object.keys(STATUS).map(value => ({ value, text: STATUS[value].label }))
        },
    },
    watch: {
        'lead.id': {
            immediate: true,
            handler(id) {
                if (id) this.load()
            },
        },
    },
    async mounted() {
        try {
            const { data } = await this.$axios.get('api/suppliers/get_supplier_type')
            this.types = data.data || []
        } catch (e) {
            this.types = []
        }
    },
    methods: {
        async load() {
            this.loading = true
            try {
                const { data } = await this.$axios.get(`admin/supplier_availability/get_requests/${this.lead.id}`)
                this.requests = data.data || []
            } finally {
                this.loading = false
            }
        },
        openRequest(item = null) {
            this.preset = {
                type_id: item ? item.supplier_type_id : null,
                supplier_ids: item ? [item.supplier_id] : [],
                product_ids: item && item.products ? item.products.map(p => p.id) : [],
            }
            this.request_dialog = true
        },
        // From the Email Supplier menu (Event → Suppliers): these suppliers ticked, these products picked.
        requestFor(supplierIds, productIds = []) {
            this.preset = { type_id: null, supplier_ids: supplierIds, product_ids: productIds }
            this.request_dialog = true
        },
        openLog() {
            this.log = {
                supplier_type_id: null, supplier_id: null, channel: 'phone', status: 'waiting', notes: '',
                requested_date: this.lead.start_date ? String(this.lead.start_date).substring(0, 10) : null,
            }
            this.log_dialog = true
            this.loadLogSuppliers()
        },
        async loadLogSuppliers() {
            this.log_loading = true
            try {
                const params = this.log.supplier_type_id ? { supplier_type_id: this.log.supplier_type_id } : {}
                const { data } = await this.$axios.get(`admin/supplier_availability/get_candidates/${this.lead.id}`, { params })
                this.log_suppliers = data.data || []
            } finally {
                this.log_loading = false
            }
        },
        async saveLog() {
            this.saving = true
            try {
                const { data } = await this.$axios.post('admin/supplier_availability/log_contact', { lead_id: this.lead.id, ...this.log })
                this.requests = data.data
                this.log_dialog = false
                this.$toast.success(data.message)
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to log contact')
            } finally {
                this.saving = false
            }
        },
        async update(item, payload) {
            this.saving = true
            try {
                const { data } = await this.$axios.post(`admin/supplier_availability/update_request/${item.id}`, payload)
                this.requests = data.data
                return true
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to update')
                return false
            } finally {
                this.saving = false
            }
        },
        openNotes(item) {
            this.notes_item = item
            this.notes_text = item.notes || ''
            this.notes_dialog = true
        },
        async saveNotes() {
            if (await this.update(this.notes_item, { notes: this.notes_text })) this.notes_dialog = false
        },
        async remove(item) {
            if (!confirm(`Delete this availability record for ${item.supplier ? item.supplier.supplier_name : 'this supplier'}?`)) return
            try {
                const { data } = await this.$axios.delete(`admin/supplier_availability/delete_request/${item.id}`)
                this.requests = data.data
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to delete')
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.settings-card {
    border-radius: 10px !important;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    overflow: hidden;
}

.card-section-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.card-section-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.card-section-subtitle {
    font-family: Manrope;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    margin-top: 2px;
}

.avail-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-family: Manrope;
    font-size: 11px;
    font-weight: 700;
}

.avail-meta {
    font-size: 12px;
    color: #7C7C7C;
}
</style>
